import { gql, type TypedDocumentNode } from "@apollo/client";
import { type GQL } from "@binale-tech/shared";

export const postingRuleCorpusStatusQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "postingRuleCorpusStatus">,
    GQL.IQueryPostingRuleCorpusStatusArgs
> = gql`
    query postingRuleCorpusStatus($companyId: ID!, $direction: PostingRuleDirection!) {
        postingRuleCorpusStatus(companyId: $companyId, direction: $direction) {
            skr
            ownRows
            pendingEmbeddings
            sharedFallbackActive
            correctionsTotal
            correctionsInPrompt
            retrieveK
            sharedFallbackBelow
            correctionsLimit
            confidenceThreshold
        }
    }
`;

export const postingRuleCounterpartySummaryQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "postingRuleCounterpartySummary">,
    GQL.IQueryPostingRuleCounterpartySummaryArgs
> = gql`
    query postingRuleCounterpartySummary(
        $companyId: ID!
        $direction: PostingRuleDirection!
        $limit: Int
        $offset: Int
    ) {
        postingRuleCounterpartySummary(companyId: $companyId, direction: $direction, limit: $limit, offset: $offset) {
            items {
                contactId
                counterpartyName
                sachkontoBase
                bu
                lineCount
                shareOfCounterparty
                lastBookingDate
            }
            total
        }
    }
`;

export const postingRuleCorrectionsQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "postingRuleCorrections">,
    GQL.IQueryPostingRuleCorrectionsArgs
> = gql`
    query postingRuleCorrections($companyId: ID!, $direction: PostingRuleDirection!, $limit: Int, $offset: Int) {
        postingRuleCorrections(companyId: $companyId, direction: $direction, limit: $limit, offset: $offset) {
            items {
                fromSachkonto
                fromBu
                toSachkonto
                toBu
                count
                lastSeen
                inPrompt
            }
            total
        }
    }
`;

export const postingRulesQuery: TypedDocumentNode<Pick<GQL.IQuery, "postingRules">, GQL.IQueryPostingRulesArgs> = gql`
    query postingRules($companyId: ID!, $direction: PostingRuleDirection!) {
        postingRules(companyId: $companyId, direction: $direction) {
            id
            direction
            contactId
            counterpartyName
            purpose
            taxRate
            categoryNum
            status
            appliedCount
            lastAppliedAt
            updatedAt
            updatedBy
            unknownKonto
        }
    }
`;

export const postingRuleSuggestionsQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "postingRuleSuggestions">,
    GQL.IQueryPostingRuleSuggestionsArgs
> = gql`
    query postingRuleSuggestions($companyId: ID!, $direction: PostingRuleDirection!) {
        postingRuleSuggestions(companyId: $companyId, direction: $direction) {
            contactId
            counterpartyName
            categoryNum
            lineCount
            shareOfCounterparty
            lastBookingDate
        }
    }
`;

// Field set duplicated (not interpolated) so validate-graphql.mjs, which skips any \${...} template, still checks these.
export const postingRuleSaveMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "postingRuleSave">,
    GQL.IMutationPostingRuleSaveArgs
> = gql`
    mutation postingRuleSave($input: PostingRuleInput!) {
        postingRuleSave(input: $input) {
            id
            direction
            contactId
            counterpartyName
            purpose
            taxRate
            categoryNum
            status
            appliedCount
            lastAppliedAt
            updatedAt
            updatedBy
            unknownKonto
        }
    }
`;

export const postingRuleSetStatusMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "postingRuleSetStatus">,
    GQL.IMutationPostingRuleSetStatusArgs
> = gql`
    mutation postingRuleSetStatus($companyId: ID!, $id: ID!, $status: PostingRuleStatus!) {
        postingRuleSetStatus(companyId: $companyId, id: $id, status: $status) {
            id
            direction
            contactId
            counterpartyName
            purpose
            taxRate
            categoryNum
            status
            appliedCount
            lastAppliedAt
            updatedAt
            updatedBy
            unknownKonto
        }
    }
`;

export const postingRuleImportSuggestionsMutation: TypedDocumentNode<
    Pick<GQL.IMutation, "postingRuleImportSuggestions">,
    GQL.IMutationPostingRuleImportSuggestionsArgs
> = gql`
    mutation postingRuleImportSuggestions($companyId: ID!, $direction: PostingRuleDirection!) {
        postingRuleImportSuggestions(companyId: $companyId, direction: $direction) {
            id
            direction
            contactId
            counterpartyName
            purpose
            taxRate
            categoryNum
            status
            appliedCount
            lastAppliedAt
            updatedAt
            updatedBy
            unknownKonto
        }
    }
`;

export const postingRuleExamplesQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "postingRuleExamples">,
    GQL.IQueryPostingRuleExamplesArgs
> = gql`
    query postingRuleExamples(
        $companyId: ID!
        $direction: PostingRuleDirection!
        $contactId: ID
        $sachkontoBase: String!
        $bu: Int!
        $limit: Int
        $offset: Int
    ) {
        postingRuleExamples(
            companyId: $companyId
            direction: $direction
            contactId: $contactId
            sachkontoBase: $sachkontoBase
            bu: $bu
            limit: $limit
            offset: $offset
        ) {
            items {
                recordId
                product
                bookingDate
                content
            }
            total
        }
    }
`;
